<template>
  <div>
    <b-row>
      <b-col>
        <search-box v-model="query" />
      </b-col>
      <b-col class="text-right">
        <b-button
          size="sm"
          variant="outline-success"
          class="mr-2"
          :disabled="!staff || !staff.length || transactionFileLoading"
          @click="downloadStaffFilmType()"
        >
          <span>{{ transactionFileLoading ? getLabel('FILE_GENERATING') : getLabel('STAFF_FILMTYPE') }}</span>
        </b-button>
        <b-button
          size="sm"
          variant="outline-success"
          class="mr-2"
          :disabled="!staff || !staff.length || staffFileLoading"
          @click="downloadStaff()"
        >
          <span>{{ staffFileLoading ? getLabel('FILE_GENERATING') : getLabel('DOWNLOAD_EXCEL') }}</span>
        </b-button>
        <b-button
          v-if="auth.user.selectedAccess.can_manage_staff"
          v-router-link="{
            name: 'staff-editor',
            query: { distributor_id, subdistributor_id, chain_id, store_id },
          }"
          size="sm"
          variant="outline-primary"
        >
          <i class="fa fa-plus"></i>&nbsp;
          <span v-label-key="'ADD_STAFF'">Add staff</span>
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="9">
        <b-table
          id="staff-table"
          ref="staffTable"
          :per-page="0"
          :current-page="currentPage"
          :items="staff"
          :fields="fields"
          :busy="loading"
          show-empty
          striped
          hover
          responsive
          @sort-changed="handleSortChange"
        >
          <template v-slot:cell(staff_id)="data">
            <router-link :to="{ name: 'staff-detail', params: data.item }">
              {{ data.item.staff_id }}
            </router-link>
          </template>
          <template v-slot:cell(staff_name)="data">
            <router-link :to="{ name: 'staff-detail', params: data.item }">
              {{ data.item.staff_name }}
            </router-link>
          </template>
          <template v-slot:cell(transactioncount)="data">
            {{ data.item.transactioncount || 0 }}
          </template>
          <template v-slot:cell(successcount)="data">
            {{ data.item.successcount || 0 }}
          </template>
          <template v-slot:cell(confirmedcount)="data">
            {{ data.item.confirmedcount || 0 }}
          </template>
          <template v-slot:cell(failedcount)="data">
            {{ data.item.failedcount || 0 }}
          </template>
          <template v-slot:table-busy>
            <loading-spinner />
          </template>
        </b-table>
        <b-pagination
          v-model="currentPage"
          :total-rows="total"
          :per-page="perPage"
          align="right"
          aria-controls="staff-table"
          @change="pageChanged"
        />
      </b-col>
      <b-col md="3">
        <b-card no-body>
          <b-card-header>
            <span v-label-key="'SHOW_CUTS_FROM_DATE_RANGE'"
              >Show Cuts From</span
            >
          </b-card-header>
          <b-card-body>
            <date-range-selector @input="loadStaff" />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import $api from '@/services/api';
import $auth from '@/services/auth';
import $excel from '@/services/excel';
import $search from '@/services/search';
import LoadingSpinner from '@/components/LoadingSpinner';
import SearchBox from '@/components/SearchBox';
import DateRangeSelector from '@/components/DateRangeSelector';
import { mapGetters } from 'vuex';

export default {
  name: 'StaffList',
  components: {
    LoadingSpinner,
    SearchBox,
    DateRangeSelector,
  },
  props: ['distributor_id', 'subdistributor_id', 'chain_id', 'store_id'],
  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
      translate: 'translate',
    }),
    fields() {
      return [
        { key: 'staff_id', label: this.getLabel('STAFF_ID'), sortable: true },
        { key: 'staff_name', label: this.getLabel('STAFF_NAME'), sortable: true },
        { key: 'store_name', label: this.getLabel('STORE'), sortable: true },
        {
          key: 'transactioncount',
          label: this.getLabel('CUTS'),
          sortable: false,
        },
        {
          key: 'successcount',
          label: this.getLabel('CUT_STATUS_CUT_SUCCESS'),
          sortable: false,
        },
        {
          key: 'confirmedcount',
          label: this.getLabel('CUT_STATUS_CUT_CONFIRMED'),
          sortable: false,
        },
        {
          key: 'failedcount',
          label: this.getLabel('CUT_STATUS_CUT_FAILED'),
          sortable: false,
        },
      ];
    },

    totalRows() {
      return (
        this.staff &&
        this.search(this.staff, this.query, ['staff_name', 'staff_id']).length
      );
    },
  },
  data() {
    return {
      auth: $auth,
      search: $search,
      query: null,
      staff: [],
      dateRange: null,
      perPage: 10,
      currentPage: 1,
      total: 0,
      orderBy: '',
      isOrderByDesc: false,
      loading: false,
      transactionFileLoading: false,
      staffFileLoading: false,
    };
  },
  watch:{
    query(newValue) {
      this.debounceSearch(newValue);
    }
  },
  beforeDestroy() {
    if (this.debounceSearch && this.debounceSearch.cancel) {
      this.debounceSearch.cancel();
    }
  },
  created() {
    this.debounceSearch = debounce(() => {
      this.currentPage = 1;
      this.refreshData();
    }, 500);
  },
  methods: {
    loadStaff(range) {
      this.dateRange = range;

      this.refreshData();
    },
    //---download staff list
    downloadStaff() {
      this.staffFileLoading = true;

      const headerKeys = [
        'STAFF_ID',
        'STAFF_NAME',
        'STORE',
        'CUTS',
        'CUT_STATUS_CUT_SUCCESS',
        'CUT_STATUS_CUT_CONFIRMED',
        'CUT_STATUS_CUT_FAILED',
      ];
      const headers = headerKeys.map(this.getLabel);
      const fields = [
        'staff_id',
        'staff_name',
        'store_name',
        'transactioncount',
        'successcount',
        'confirmedcount',
        'failedcount',
      ];

      const tempQuery = this.getListQuery();

      $api
        .get('/v1/dashboard/retailstaff' + tempQuery)
        .then((response) => {
          $excel.export(response.data, fields, 'staff', headers);
        })
        .catch((e) => {
          alert(`Error: ${e}`);
        })
        .finally(() => {
          this.staffFileLoading = false;
        });
    },
    //--download staff filmtype transaction
    downloadStaffFilmType(){
      this.transactionFileLoading = true;

      const headerKeys = [
        'STORE_ID',
        'STORE_NAME',
        'CHAIN_NAME',
        'FILMTYPE_NAME',
        'FILMTYPE_DESCRIPTION',
        'PUBLIC_STATUS',
        'STAFF_ID',
        'STAFF_NAME',
        'CREATED',
      ];
      const headers = headerKeys.map(this.getLabel);

      const fields = [
        'store_id',
        'store_name',
        'chain_name',
        'filmtype_name',
        'filmtype_description',
        'public_status',
        'staff_id',
        'staff_name',
        'created'
      ];

      const tempQuery = this.getListQuery();

      $api
        .get('/v1/dashboard/transactions' + tempQuery)
        .then((response) => {
          $excel.export(response, fields, 'staff-filmtype', headers);
        })
        .catch((e) => {
          alert(`Error: ${e}`);
        })
        .finally(() => {
          this.transactionFileLoading = false;
        });
    },
    //----------
    pageChanged(page) {
      this.currentPage = page;
      this.refreshData();
    },
    handleSortChange(e){
      this.orderBy = e.sortBy;
      this.isOrderByDesc = e.sortDesc;
      this.refreshData();
    },
    refreshData() {
      this.loading = true;
      this.staff = [];

      let tempQuery = this.getListQuery();

      tempQuery += `&skip=${(this.currentPage - 1) * this.perPage}&take=${this.perPage}`;
      
      $api
        .get('/v1/dashboard/retailstaff' + tempQuery)
        .then((response) => {
          this.staff = response.data;
          this.total = response.total;
          this.$emit('loaded', response.data);
        })
        .catch(() => {
        })
        .finally(() => {
          this.loading = false;
          this.$refs.staffTable.refresh();
        });
    },
    getListQuery(){
      const payload = {
        distributor_id: this.distributor_id,
        subdistributor_id: this.subdistributor_id,
        chain_id: this.chain_id,
        store_id: this.store_id,
        _start: this.dateRange ? this.dateRange.start : null,
        _end: this.dateRange ? this.dateRange.end : null,
        timezone: this.dateRange ? this.dateRange.timezone : null,
        orderBy: this.orderBy? this.orderBy : null,
        isOrderByDesc: this.orderBy ? this.isOrderByDesc : null,
        searchKey: this.query ? this.query : null,
      };

      let tempQuery = "?";
      for (const key in payload) {
        if (payload[key] !== null && payload[key] !== undefined) {
          tempQuery += `&${key}=${payload[key]}`;
        }
      }
      
      return tempQuery;
    }
  },
};
</script>
<style scoped>
a {
  color: black;
}
</style>
